import { Controller, useFormContext } from "react-hook-form";

import { DialogActions, DialogBackButton } from "ui/dialog";
import { LoadingButton, useAsyncSubmit } from "ui/button";
import { Select, type OptionType } from "ui/select";
import { TextField } from "ui/text-field";

import { useCampaignFromTemplateContext } from "components/modals/create-campaign-from-template-modal/context/campaign-from-template-context";
import { useCreateCampaignFromTemplate } from "components/modals/create-campaign-from-template-modal/hooks/use-create-campaign-from-template";

import type { CreateCampaignFromTemplateFormData } from "components/modals/create-campaign-from-template-modal/create-campaign-from-template-modal.types";

const convertFormValueToOptions = (value: unknown, options: OptionType[]): OptionType[] => {
  return Array.isArray(value)
    ? value
        .map((val) => options.find((opt) => opt.value === val))
        .filter((opt): opt is OptionType => opt !== undefined)
    : [];
};

const CampaignDetailsStep = () => {
  const { audienceTypes } = useCampaignFromTemplateContext();

  const {
    control,
    formState: { errors },
  } = useFormContext<CreateCampaignFromTemplateFormData>();

  return (
    <div className="flex flex-col gap-2">
      <p className="mb-4">Please edit campaign details as needed and proceed.</p>
      <Controller
        name="campaignName"
        control={control}
        render={({ field: { onChange, value, ...field } }) => (
          <TextField
            label={<span className="font-bold">Campaign name</span>}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            required
            errMessage={errors.campaignName?.message as string}
            {...field}
          />
        )}
      />
      <Controller
        name="cdnSubdirectoryName"
        control={control}
        render={({ field: { onChange, value, ...field } }) => (
          <TextField
            label={<span className="font-bold">Campaign CDN profile</span>}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            helperText="Enter a profile name with no spaces. This name will be used to organize this campaign's digital assets and landing pages at its destination URL."
            errMessage={errors.cdnSubdirectoryName?.message as string}
            {...field}
          />
        )}
      />
      <Controller
        name="audiences"
        control={control}
        render={({ field: { onChange, value, ...field } }) => {
          const audienceOptions: OptionType[] = audienceTypes.map((audience) => ({
            label: audience.description || "",
            value: audience.name || "",
          }));

          const selectedOptions = convertFormValueToOptions(value, audienceOptions);

          return (
            <Select
              label={<span className="font-bold">Audience</span>}
              selectName="audiences"
              multiple={true}
              options={audienceOptions}
              ariaLabel="Select audiences"
              value={selectedOptions}
              onChange={(selected) => onChange(selected.map((option) => option.value))}
              {...field}
            />
          );
        }}
      />
    </div>
  );
};

export const CampaignDetailsStepActions = () => {
  const { back } = useCampaignFromTemplateContext();
  const { createCampaign } = useCreateCampaignFromTemplate();
  const {
    formState: { isValid },
    handleSubmit,
  } = useFormContext<CreateCampaignFromTemplateFormData>();

  const { submit, submitState } = useAsyncSubmit(
    async (data: CreateCampaignFromTemplateFormData) => {
      await createCampaign(data);
    },
    {
      onError: (error) => {
        console.error("Failed to create campaign:", error);
      },
    },
  );

  return (
    <DialogActions
      type="steps"
      back={<DialogBackButton onClick={back}>Back to details</DialogBackButton>}
      primary={
        <LoadingButton
          size="large"
          disabled={!isValid}
          onClick={handleSubmit(submit)}
          state={submitState}
          loadingMessage="Creating campaign, this may take a while..."
          successMessage="Campaign created"
          errorMessage="Error. Try again.">
          Create campaign
        </LoadingButton>
      }
    />
  );
};

export default CampaignDetailsStep;
